/* Native OnboardingGate + ProfileScreen controller. Auth uses the same
   Supabase project and customer identity as the app. Store writes remain
   limited to owner-protected Supabase rows; Shopify Admin is never exposed. */

(function () {
  var SESSION_KEY = 'gettmi:session:v1';
  var OTP_RESEND_COOLDOWN_SECONDS = 60;
  var resendTimer = null;
  var pendingEmail = '';
  var recoverySession = null;
  var routeId = 0;

  function config() {
    var el = document.querySelector('[data-gettmi-supabase]');
    return el ? { url: el.dataset.supabaseUrl, key: el.dataset.supabaseKey } : null;
  }

  function overlay() {
    return document.querySelector('[data-gettmi-settings]');
  }

  function readSession() {
    try {
      var raw = window.localStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeSession(session) {
    try {
      if (session) window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      else window.localStorage.removeItem(SESSION_KEY);
    } catch (error) {
      /* Private mode can continue with the current page session only. */
    }
  }

  /* Mirrors lib/email.js in the app so both surfaces refuse the same addresses. */
  var DISPOSABLE_EMAIL_DOMAINS = [
    '10minutemail.com',
    'guerrillamail.com',
    'mailinator.com',
    'sharklasers.com',
    'tempmail.com',
    'temp-mail.org',
    'throwawaymail.com',
    'yopmail.com'
  ];

  var COMMON_EMAIL_DOMAINS = [
    'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'hotmail.co.uk',
    'live.co.uk', 'live.com', 'yahoo.com', 'yahoo.co.uk', 'icloud.com', 'me.com',
    'aol.com', 'btinternet.com', 'sky.com'
  ];

  function emailDomain(email) {
    return String(email || '').trim().toLowerCase().split('@')[1] || '';
  }

  function isValidEmailAddress(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim().toLowerCase());
  }

  function isDisposableEmailDomain(domain) {
    return DISPOSABLE_EMAIL_DOMAINS.indexOf(String(domain || '').toLowerCase()) !== -1;
  }

  function editDistance(a, b) {
    var previous = [];
    var i;
    var j;
    for (j = 0; j <= b.length; j += 1) previous[j] = j;
    for (i = 1; i <= a.length; i += 1) {
      var current = [i];
      for (j = 1; j <= b.length; j += 1) {
        current[j] = Math.min(
          previous[j] + 1,
          current[j - 1] + 1,
          previous[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1)
        );
      }
      previous = current;
    }
    return previous[b.length];
  }

  /* Offers a correction for near-miss domains. Never blocks: a real address one edit
     away from a common provider still submits exactly as typed. */
  function suggestedEmail(email) {
    var value = String(email || '').trim().toLowerCase();
    var domain = emailDomain(value);
    if (!domain || COMMON_EMAIL_DOMAINS.indexOf(domain) !== -1) return '';
    var best = '';
    var bestDistance = 3;
    COMMON_EMAIL_DOMAINS.forEach(function (candidate) {
      if (candidate.charAt(0) !== domain.charAt(0)) return;
      var distance = editDistance(domain, candidate);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = candidate;
      }
    });
    if (!best || bestDistance > 2) return '';
    return value.split('@')[0] + '@' + best;
  }

  function signupFailureMessage(error) {
    var code = error && (error.error_code || error.code);
    if (code === 'weak_password') {
      return 'That password is too easy to guess, or has appeared in a known breach. Choose a different one.';
    }
    if (code === 'user_already_exists' || code === 'email_exists') {
      return 'That email already has a Gettmi account. Log in instead, or use Forgot password.';
    }
    if (code === 'over_email_send_rate_limit' || code === 'over_request_rate_limit') {
      return 'Too many attempts just now. Wait a minute and try again.';
    }
    if (code === 'email_address_invalid') {
      return 'Enter a valid email address.';
    }
    return 'We couldn’t create the account. Please try again in a moment.';
  }

  function renderEmailSuggestion(form) {
    var field = form && form.querySelector('input[name="email"]');
    var hint = form && form.querySelector('[data-gettmi-email-suggest]');
    if (!field || !hint) return;
    var suggestion = suggestedEmail(field.value);
    hint.dataset.suggestion = suggestion;
    hint.textContent = suggestion ? 'Did you mean ' + suggestion + '?' : '';
    hint.hidden = !suggestion;
  }

  function dispatchSignedOut(session) {
    document.dispatchEvent(new CustomEvent('gettmi:signed-out', {
      detail: { userId: session && session.user_id || '' }
    }));
  }

  function request(cfg, path, options) {
    var settings = options || {};
    var headers = {
      apikey: cfg.key,
      Authorization: 'Bearer ' + (settings.token || cfg.key)
    };
    if (settings.body !== undefined) headers['Content-Type'] = 'application/json';

    return window.fetch(cfg.url + path, {
      method: settings.method || 'POST',
      headers: headers,
      body: settings.body === undefined ? undefined : JSON.stringify(settings.body)
    });
  }

  function sessionFromAuth(data, fallbackEmail) {
    var user = data && data.user;
    if (!data || !data.access_token) return null;
    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: data.expires_at || (Math.floor(Date.now() / 1000) + (data.expires_in || 3600)),
      email: (user && user.email) || fallbackEmail || '',
      user_id: user && user.id
    };
  }

  function setBusy(form, busy, busyLabel) {
    if (!form) return;
    var submit = form.querySelector('[type="submit"]');
    if (!submit) return;
    if (!submit.dataset.defaultLabel) submit.dataset.defaultLabel = submit.textContent;
    submit.disabled = busy;
    submit.textContent = busy ? busyLabel : submit.dataset.defaultLabel;
  }

  function showMessage(step, message) {
    var root = overlay();
    var scope = root && root.querySelector('[data-gettmi-auth-step="' + step + '"]');
    var el = scope && scope.querySelector('[data-gettmi-auth-message]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  function showAuthStep(step) {
    var root = overlay();
    if (!root) return;
    root.querySelectorAll('[data-gettmi-auth-step]').forEach(function (el) {
      el.hidden = el.dataset.gettmiAuthStep !== step;
    });
    showMessage(step, '');

    if (step === 'password') {
      root.querySelectorAll('[data-gettmi-auth-email-label]').forEach(function (el) {
        el.textContent = pendingEmail;
      });
      var password = root.querySelector('[data-gettmi-auth-password-form] input[name="password"]');
      if (password) window.setTimeout(function () { password.focus(); }, 0);
    }
    if (step === 'code') {
      var codeEmail = root.querySelector('[data-gettmi-auth-code-email]');
      if (codeEmail) codeEmail.textContent = pendingEmail;
      resetCodeEntry('code');
      var code = root.querySelector('[data-gettmi-auth-code-form] input[name="code"]');
      if (code) window.setTimeout(function () { code.focus(); }, 0);
    }
    if (step === 'signup-pending') {
      var pendingLabel = root.querySelector('[data-gettmi-auth-pending-email]');
      if (pendingLabel) pendingLabel.textContent = pendingEmail;
      resetCodeEntry('signup-pending');
      var confirmInput = root.querySelector('[data-gettmi-auth-confirm-form] input[name="code"]');
      if (confirmInput) window.setTimeout(function () { confirmInput.focus(); }, 0);
    }
    if (step === 'recovery') {
      var recoveryPassword = root.querySelector('[data-gettmi-auth-recovery-form] input[name="password"]');
      if (recoveryPassword) window.setTimeout(function () { recoveryPassword.focus(); }, 0);
    }
  }

  /* Two steps enter a six-digit code, signing in and confirming a new account,
     so every helper works against one step's grid rather than the first on the page. */
  function codeElements(step) {
    var root = overlay();
    var scope = root && root.querySelector('[data-gettmi-auth-step="' + (step || 'code') + '"]');
    var row = scope && scope.querySelector('[data-gettmi-auth-code-row]');
    if (!row) return null;
    return {
      row: row,
      input: row.querySelector('[name="code"]'),
      boxes: row.querySelectorAll('[data-gettmi-auth-code-box]')
    };
  }

  function stepOf(element) {
    var section = element && element.closest && element.closest('[data-gettmi-auth-step]');
    return section ? section.dataset.gettmiAuthStep : '';
  }

  function renderCodeBoxes(step) {
    var parts = codeElements(step);
    if (!parts || !parts.input) return;
    var value = parts.input.value;
    Array.prototype.forEach.call(parts.boxes, function (box, index) {
      box.textContent = value.charAt(index) || '';
      box.classList.toggle('is-active', index === value.length && value.length < 6);
    });
  }

  function setCodeError(step, hasError) {
    var parts = codeElements(step);
    if (parts) parts.row.classList.toggle('has-error', Boolean(hasError));
  }

  function resetCodeEntry(step) {
    var parts = codeElements(step);
    if (!parts || !parts.input) return;
    parts.input.value = '';
    setCodeError(step, false);
    renderCodeBoxes(step);
  }

  function startResendCooldown(button) {
    var remaining = OTP_RESEND_COOLDOWN_SECONDS;
    window.clearInterval(resendTimer);
    button.disabled = true;
    button.textContent = 'Resend code in ' + remaining + 's';
    resendTimer = window.setInterval(function () {
      remaining -= 1;
      if (remaining > 0) {
        button.textContent = 'Resend code in ' + remaining + 's';
        return;
      }
      window.clearInterval(resendTimer);
      resendTimer = null;
      button.disabled = false;
      button.textContent = 'Resend code';
    }, 1000);
  }

  function showSignedOut() {
    var root = overlay();
    if (!root) return;
    routeId += 1;
    root.querySelector('[data-gettmi-auth-gate]').hidden = false;
    root.querySelector('[data-gettmi-profile]').hidden = true;
    var accountCenter = root.querySelector('[data-gettmi-account-center]');
    if (accountCenter) accountCenter.hidden = true;
    document.documentElement.classList.remove('gettmi-profile-open');
    showAuthStep('welcome');
  }

  function hasValues(value) {
    return Array.isArray(value) && value.filter(Boolean).length > 0;
  }

  function profileIsReady(profile) {
    return Boolean(
      hasValues(profile.favourite_brands) &&
      (hasValues(profile.favourite_creator_ids) || hasValues(profile.suggested_creator_handles)) &&
      hasValues(profile.opening_feed) &&
      (hasValues(profile.sizes) || hasValues(profile.shoe_sizes)) &&
      hasValues(profile.occasions) &&
      hasValues(profile.budgets)
    );
  }

  function renderSharedProfileAvatars(profile, session) {
    var photoUrl = profile && profile.profile_photo_url ? String(profile.profile_photo_url) : '';
    var initialValue = profile && (profile.first_name || profile.username);
    var initial = String(initialValue || (session && session.email) || 'G').trim().charAt(0).toUpperCase() || 'G';

    document.querySelectorAll('[data-gettmi-profile-avatar]').forEach(function (mount) {
      var image = mount.querySelector('[data-gettmi-profile-avatar-image]');
      var fallback = mount.querySelector('[data-gettmi-profile-avatar-initial]');
      if (fallback) {
        fallback.textContent = initial;
        fallback.hidden = Boolean(photoUrl);
      }
      if (!image) return;
      image.onerror = function () {
        image.hidden = true;
        image.removeAttribute('src');
        if (fallback) fallback.hidden = false;
      };
      if (photoUrl) {
        image.src = photoUrl;
        image.hidden = false;
      } else {
        image.hidden = true;
        image.removeAttribute('src');
      }
    });
  }

  function appGateEnabled(surface) {
    return Boolean(
      window.GettmiAppGate &&
      typeof window.GettmiAppGate.isEnabled === 'function' &&
      window.GettmiAppGate.isEnabled(surface)
    );
  }

  function desktopAccountMode() {
    return window.matchMedia('(min-width: 1200px)').matches;
  }

  function updateProfileData(cfg, session) {
    if (!cfg || !session || !session.access_token || !session.user_id) return;
    var styleGated = appGateEnabled('style_profile');
    var profileFields = styleGated
      ? 'username,first_name,last_name,profile_photo_url'
      : 'style_profile_ready,username,first_name,last_name,profile_photo_url,favourite_brands,favourite_creator_ids,suggested_creator_handles,opening_feed,sizes,shoe_sizes,occasions,budgets';
    return request(cfg, '/rest/v1/profiles?select=' + profileFields + '&id=eq.' + encodeURIComponent(session.user_id) + '&limit=1', {
      method: 'GET',
      token: session.access_token
    }).then(function (response) {
      if (!response.ok) return null;
      return response.json();
    }).then(function (rows) {
      var profile = Array.isArray(rows) && rows.length ? rows[0] : {};
      renderSharedProfileAvatars(profile, session);
      document.dispatchEvent(new CustomEvent('gettmi:profile-read', {
        detail: { profile: profile, session: session }
      }));
      var root = overlay();
      if (!root) return;
      var profileView = root.querySelector('[data-gettmi-profile]');
      var missingProfile = Boolean(
        profileView && profileView.dataset.gettmiProfileMissing === 'true'
      );
      var ready = styleGated ? !missingProfile : profileIsReady(profile);
      var alert = root.querySelector('[data-gettmi-profile-attention]');
      var dot = root.querySelector('[data-gettmi-style-attention]');
      if (alert) alert.hidden = ready;
      if (dot) dot.hidden = ready;
      var attentionTitle = root.querySelector('[data-gettmi-style-attention-title]');
      var attentionCopy = root.querySelector('[data-gettmi-style-attention-copy]');
      if (styleGated) {
        if (attentionTitle) attentionTitle.textContent = 'Finish Your Style Profile in the App';
        if (attentionCopy) attentionCopy.textContent = 'Set up your personal style space in the GETTMI app.';
      }
      var fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
      var displayName = profile.username || fullName || session.email || '';
      var name = root.querySelector('[data-gettmi-account-name]');
      var meta = root.querySelector('[data-gettmi-account-meta]');
      var initial = root.querySelector('[data-gettmi-account-initial]');
      var avatar = root.querySelector('[data-gettmi-account-avatar]');
      if (name) name.textContent = displayName;
      if (meta) meta.textContent = fullName;
      if (initial) {
        initial.textContent = String(profile.first_name || profile.username || session.email || 'G').trim().charAt(0).toUpperCase() || 'G';
        initial.hidden = Boolean(profile.profile_photo_url);
      }
      if (avatar) {
        avatar.src = profile.profile_photo_url || '';
        avatar.hidden = !profile.profile_photo_url;
      }
    }).catch(function () {
      /* Keep the native attention state if the protected profile read fails. */
    });
  }

  function showProfile(session, missingProfile) {
    var root = overlay();
    if (!root) return;
    if (desktopAccountMode()) {
      root.hidden = true;
      root.querySelector('[data-gettmi-auth-gate]').hidden = true;
      root.querySelector('[data-gettmi-profile]').hidden = true;
      var desktopAccountCenter = root.querySelector('[data-gettmi-account-center]');
      if (desktopAccountCenter) desktopAccountCenter.hidden = true;
      document.documentElement.classList.remove('gettmi-overlay-open', 'gettmi-profile-open');
      document.dispatchEvent(new CustomEvent('gettmi:desktop-account-ready', {
        detail: { session: session, missingProfile: Boolean(missingProfile) }
      }));
      updateProfileData(config(), session);
      return;
    }
    root.querySelector('[data-gettmi-auth-gate]').hidden = true;
    var profileView = root.querySelector('[data-gettmi-profile]');
    profileView.hidden = false;
    profileView.dataset.gettmiProfileMissing = String(Boolean(missingProfile));
    var accountCenter = root.querySelector('[data-gettmi-account-center]');
    if (accountCenter) accountCenter.hidden = true;
    document.documentElement.classList.add('gettmi-profile-open');
    updateProfileData(config(), session);
  }

  function showOnboarding(session) {
    var root = overlay();
    if (!root) return;
    root.hidden = false;
    document.documentElement.classList.add('gettmi-overlay-open');
    root.querySelector('[data-gettmi-auth-gate]').hidden = false;
    root.querySelector('[data-gettmi-profile]').hidden = true;
    var accountCenter = root.querySelector('[data-gettmi-account-center]');
    if (accountCenter) accountCenter.hidden = true;
    document.documentElement.classList.remove('gettmi-profile-open');
    document.dispatchEvent(new CustomEvent('gettmi:onboarding-required', {
      detail: { session: session }
    }));
  }

  function routeSignedIn(session, options) {
    var cfg = config();
    if (!cfg || !session || !session.access_token || !session.user_id) {
      showSignedOut();
      return Promise.resolve(null);
    }
    /* Signing in returns the shopper to what they were browsing, as entering the app does. */
    var returnToBrowsing = Boolean(options && options.fresh) && !desktopAccountMode();
    routeId += 1;
    var currentRouteId = routeId;

    function enterAccount(missingProfile) {
      if (!returnToBrowsing) {
        showProfile(session, missingProfile);
        return;
      }
      closeSettings();
      updateProfileData(cfg, session);
    }

    return request(
      cfg,
      '/rest/v1/profiles?select=id&id=eq.' + encodeURIComponent(session.user_id) + '&limit=1',
      { method: 'GET', token: session.access_token }
    ).then(function (response) {
      if (!response.ok) throw new Error();
      return response.json();
    }).then(function (rows) {
      if (currentRouteId !== routeId) return null;
      if (desktopAccountMode()) {
        showProfile(session, !(Array.isArray(rows) && rows.length));
      } else if (Array.isArray(rows) && rows.length) {
        enterAccount(false);
      } else {
        showOnboarding(session);
      }
      return session;
    }).catch(function () {
      if (currentRouteId !== routeId) return null;
      /* A network or policy failure must never be interpreted as a missing row. */
      enterAccount(false);
      return session;
    });
  }

  function showAccountCenter() {
    var root = overlay();
    if (!root) return;
    root.querySelector('[data-gettmi-profile]').hidden = true;
    root.querySelector('[data-gettmi-account-center]').hidden = false;
    document.documentElement.classList.remove('gettmi-profile-open');
  }

  function closeAccountCenter() {
    var root = overlay();
    if (!root) return;
    root.querySelector('[data-gettmi-account-center]').hidden = true;
    root.querySelector('[data-gettmi-profile]').hidden = false;
    document.documentElement.classList.add('gettmi-profile-open');
  }

  function refreshExpiredSession(cfg, session) {
    if (!session || !session.refresh_token) return Promise.resolve(null);
    return request(cfg, '/auth/v1/token?grant_type=refresh_token', {
      body: { refresh_token: session.refresh_token }
    }).then(function (response) {
      if (!response.ok) return null;
      return response.json();
    }).then(function (data) {
      var refreshed = sessionFromAuth(data, session.email);
      if (!refreshed) return null;
      if (!refreshed.user_id) refreshed.user_id = session.user_id;
      writeSession(refreshed);
      return refreshed;
    }).catch(function () {
      return null;
    });
  }

  function validSession() {
    var session = readSession();
    if (!session || !session.access_token) return Promise.resolve(null);
    if (!session.expires_at || session.expires_at * 1000 > Date.now() + 30000) {
      return Promise.resolve(session);
    }
    var cfg = config();
    if (!cfg) return Promise.resolve(null);
    return refreshExpiredSession(cfg, session).then(function (refreshed) {
      if (!refreshed) writeSession(null);
      return refreshed;
    });
  }

  function refreshState() {
    return validSession().then(function (session) {
      if (session) return routeSignedIn(session);
      showSignedOut();
      return null;
    });
  }

  /* Providers must return to a real storefront page. The theme has no /auth/callback
     route, and Supabase falls back to its own Site URL for any target it does not allow. */
  function localeRootUrl() {
    var rootPath = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    var url = new URL(rootPath, window.location.origin);
    var previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (!previewThemeId && window.Shopify && window.Shopify.theme) {
      previewThemeId = window.Shopify.theme.id;
    }
    if (previewThemeId) url.searchParams.set('preview_theme_id', String(previewThemeId));
    return url;
  }

  function authCallbackUrl() {
    return localeRootUrl().toString();
  }

  function recoveryCallbackUrl() {
    var url = localeRootUrl();
    url.searchParams.set('gettmi_auth', 'recovery');
    return url.toString();
  }

  function cleanRecoveryUrl() {
    var url = new URL(window.location.href);
    url.searchParams.delete('gettmi_auth');
    url.hash = '';
    window.history.replaceState({}, '', url.pathname + url.search);
  }

  function readOAuthCallback() {
    var params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    var recovery = params.get('type') === 'recovery' ||
      new URL(window.location.href).searchParams.get('gettmi_auth') === 'recovery';
    if (!params.get('access_token')) {
      return Promise.resolve(recovery ? {
        error: 'This password link is missing, expired or has already been used. Request a new one from Log in.',
        recovery: true,
        session: null
      } : null);
    }
    var cfg = config();
    if (!cfg) return Promise.resolve(null);

    var partial = {
      access_token: params.get('access_token'),
      refresh_token: params.get('refresh_token'),
      expires_at: Math.floor(Date.now() / 1000) + Number(params.get('expires_in') || 3600),
      email: '',
      user_id: ''
    };
    return request(cfg, '/auth/v1/user', { method: 'GET', token: partial.access_token })
      .then(function (response) {
        if (!response.ok) throw new Error();
        return response.json();
      })
      .then(function (user) {
        partial.email = user.email || '';
        partial.user_id = user.id || '';
        writeSession(partial);
        window.history.replaceState({}, '', window.location.pathname + window.location.search);
        return { recovery: recovery, session: partial };
      })
      .catch(function () {
        return recovery ? {
          error: 'This password link is missing, expired or has already been used. Request a new one from Log in.',
          recovery: true,
          session: null
        } : null;
      });
  }

  /* Two-way merge with the app's owner-protected bag_items table. */
  function syncBag(cfg, session, options) {
    if (!window.GettmiBag || !session || !session.access_token || !session.user_id) {
      return Promise.resolve(false);
    }
    if (window.GettmiBag.prepareForSession) {
      window.GettmiBag.prepareForSession(session.user_id);
    }
    return window.fetch(
      cfg.url +
        '/rest/v1/bag_items?select=variant_id,product_handle,product_path,product_title,vendor,image,image_alt,price,size,quantity,status,button_url,updated_at&deleted_at=is.null&user_id=eq.' +
        encodeURIComponent(session.user_id) + '&order=updated_at.desc',
      { headers: { apikey: cfg.key, Authorization: 'Bearer ' + session.access_token } }
    ).then(function (response) {
      if (!response || !response.ok) return null;
      return response.json();
    }).then(function (rows) {
      if (!Array.isArray(rows)) return false;
      return Promise.resolve(window.GettmiBag.mergeRemote(rows, options)).then(function (result) {
        return result !== false;
      });
    }).catch(function () {
      /* The local bag remains intact if remote sync is unavailable. */
      return false;
    });
  }

  function syncFavourites(session, options) {
    if (
      !window.GettmiFavourites ||
      typeof window.GettmiFavourites.syncFromAccount !== 'function'
    ) {
      return Promise.resolve(false);
    }
    if (window.GettmiFavourites.prepareForSession) {
      window.GettmiFavourites.prepareForSession(session.user_id);
    }
    return Promise.resolve(window.GettmiFavourites.syncFromAccount(options)).then(function (result) {
      return result === true;
    }).catch(function () {
      return false;
    });
  }

  function completeFreshSignIn(session) {
    document.dispatchEvent(new CustomEvent('gettmi:signed-in', {
      detail: { session: session, syncManaged: true }
    }));
    return Promise.all([
      syncBag(config(), session, { freshSignIn: true }),
      syncFavourites(session, { freshSignIn: true })
    ]).then(function (results) {
      var success = results[0] === true && results[1] === true;
      document.dispatchEvent(new CustomEvent('gettmi:sync-complete', {
        detail: { success: success }
      }));
      return success;
    });
  }

  function finishSignIn(data, fallbackEmail) {
    var session = sessionFromAuth(data, fallbackEmail);
    if (!session) throw new Error();
    writeSession(session);
    return routeSignedIn(session, { fresh: true }).then(function (routedSession) {
      if (!routedSession) return null;
      return completeFreshSignIn(session).then(function () {
        return session;
      });
    });
  }

  function openSettings() {
    var root = overlay();
    if (!root) return;
    /* Paint the pane the stored session already implies so the login step never flashes;
       refreshState still verifies the session and corrects this if it has expired. */
    var stored = readSession();
    if (stored && stored.access_token && !desktopAccountMode()) {
      root.querySelector('[data-gettmi-auth-gate]').hidden = true;
      var storedProfile = root.querySelector('[data-gettmi-profile]');
      if (storedProfile) storedProfile.hidden = false;
      document.documentElement.classList.add('gettmi-profile-open');
    }
    root.hidden = false;
    document.documentElement.classList.add('gettmi-overlay-open');
    return refreshState();
  }

  function closeSettings() {
    var root = overlay();
    if (root) root.hidden = true;
    document.documentElement.classList.remove('gettmi-overlay-open', 'gettmi-profile-open');
  }

  function filterProfile(query) {
    var root = overlay();
    var profile = root && root.querySelector('[data-gettmi-profile]');
    if (!profile) return;
    var clean = String(query || '').trim().toLowerCase();
    var anyVisible = false;

    profile.querySelectorAll('[data-gettmi-profile-search-item]').forEach(function (item) {
      var match = !clean || String(item.dataset.gettmiProfileSearchItem || '').indexOf(clean) !== -1;
      item.hidden = !match;
      if (match) anyVisible = true;
    });

    profile.querySelectorAll('[data-gettmi-profile-section]').forEach(function (section) {
      var title = String(section.dataset.gettmiProfileSectionTitle || '');
      var titleMatches = !clean || title.indexOf(clean) !== -1;
      var rows = section.querySelectorAll('[data-gettmi-profile-search-item]');
      if (titleMatches) rows.forEach(function (row) { row.hidden = false; });
      var visibleRows = Array.prototype.some.call(rows, function (row) { return !row.hidden; });
      section.hidden = !(titleMatches || visibleRows);
      if (!section.hidden) anyVisible = true;
    });

    var attention = profile.querySelector('[data-gettmi-profile-attention]');
    if (attention && !attention.hidden) {
      var attentionMatches = !clean || 'style profile needs attention favourite brands creators style icons opening feed sizes what you shop for price range'.indexOf(clean) !== -1;
      attention.style.display = attentionMatches ? '' : 'none';
      if (attentionMatches) anyVisible = true;
    }
    var empty = profile.querySelector('[data-gettmi-profile-no-results]');
    if (empty) empty.hidden = anyVisible;
  }

  document.addEventListener('click', function (event) {
    var target = event.target;

    if (target.closest('[data-gettmi-open-settings]')) {
      openSettings();
      return;
    }
    if (target.closest('[data-gettmi-settings-close]')) {
      closeSettings();
      return;
    }
    if (target.closest('[data-gettmi-open-account-center]')) {
      showAccountCenter();
      return;
    }
    if (target.closest('[data-gettmi-close-account-center]')) {
      closeAccountCenter();
      return;
    }
    if (target.closest('[data-gettmi-auth-show-welcome]')) {
      showAuthStep('welcome');
      return;
    }
    if (target.closest('[data-gettmi-auth-show-signup]')) {
      showAuthStep('signup');
      return;
    }
    if (target.closest('[data-gettmi-auth-show-password]')) {
      showAuthStep('password');
      return;
    }
    var passwordToggle = target.closest('[data-gettmi-password-toggle]');
    if (passwordToggle) {
      var passwordInput = passwordToggle.parentElement.querySelector('input');
      if (passwordInput) {
        var showing = passwordInput.type === 'text';
        passwordInput.type = showing ? 'password' : 'text';
        passwordToggle.classList.toggle('is-visible', !showing);
        passwordToggle.setAttribute('aria-label', showing ? 'Show password' : 'Hide password');
      }
      return;
    }
    var social = target.closest('[data-gettmi-auth-social]');
    if (social) {
      var cfg = config();
      if (!cfg) return;
      var provider = social.dataset.gettmiAuthSocial;
      var authorize = new URL(cfg.url + '/auth/v1/authorize');
      authorize.searchParams.set('provider', provider);
      /* Google silently reuses whichever account the browser is already signed in to, which
         reads as a failed sign-out and blocks switching accounts. Always offer the chooser. */
      if (provider === 'google') authorize.searchParams.set('prompt', 'select_account');
      authorize.searchParams.set('redirect_to', authCallbackUrl());
      window.location.assign(authorize.toString());
      return;
    }
    var suggest = target.closest('[data-gettmi-email-suggest]');
    if (suggest) {
      var suggestForm = suggest.closest('form');
      var suggestField = suggestForm && suggestForm.querySelector('input[name="email"]');
      if (suggestField && suggest.dataset.suggestion) {
        suggestField.value = suggest.dataset.suggestion;
        showMessage('signup', '');
        renderEmailSuggestion(suggestForm);
      }
      return;
    }
    if (target.closest('[data-gettmi-auth-code-row]')) {
      var focusParts = codeElements(stepOf(target));
      if (focusParts && focusParts.input) focusParts.input.focus();
      return;
    }
    var resendSignup = target.closest('[data-gettmi-auth-resend-signup]');
    if (resendSignup) {
      var signupCfg = config();
      if (!signupCfg || !pendingEmail || resendSignup.disabled) return;
      resendSignup.disabled = true;
      resendSignup.textContent = 'Sending...';
      showMessage('signup-pending', '');
      request(signupCfg, '/auth/v1/resend', {
        body: { type: 'signup', email: pendingEmail, email_redirect_to: authCallbackUrl() }
      }).then(function (response) {
        if (!response.ok) throw new Error();
        showMessage('signup-pending', 'Confirmation email resent.');
      }).catch(function () {
        showMessage('signup-pending', 'The confirmation email could not be resent yet. Please try again.');
      }).finally(function () {
        resendSignup.disabled = false;
        resendSignup.textContent = 'Resend confirmation email';
      });
      return;
    }
    var resend = target.closest('[data-gettmi-auth-resend-code]');
    if (resend) {
      var resendCfg = config();
      if (!resendCfg || !pendingEmail || resend.disabled) return;
      resend.disabled = true;
      resend.textContent = 'Sending...';
      request(resendCfg, '/auth/v1/otp', {
        body: { email: pendingEmail, create_user: false, email_redirect_to: authCallbackUrl() }
      }).then(function (response) {
        if (!response.ok) throw new Error();
        resetCodeEntry('code');
        startResendCooldown(resend);
      }).catch(function () {
        showMessage('code', 'We couldn’t send a new code. Please wait a moment and try again.');
        resend.disabled = false;
        resend.textContent = 'Resend code';
      });
      return;
    }
    if (target.closest('[data-gettmi-auth-send-code]')) {
      var otpCfg = config();
      if (!otpCfg || !pendingEmail) return;
      var send = target.closest('[data-gettmi-auth-send-code]');
      send.disabled = true;
      send.textContent = 'Sending...';
      request(otpCfg, '/auth/v1/otp', {
        body: { email: pendingEmail, create_user: false, email_redirect_to: authCallbackUrl() }
      }).then(function (response) {
        if (!response.ok) throw new Error();
        showAuthStep('code');
      }).catch(function () {
        showMessage('password', 'We couldn’t send a code. Please wait a moment and try again.');
      }).finally(function () {
        send.disabled = false;
        send.textContent = 'Send one-time email code';
      });
      return;
    }
    if (target.closest('[data-gettmi-auth-forgot]')) {
      var recoveryCfg = config();
      if (!recoveryCfg || !pendingEmail) return;
      request(recoveryCfg, '/auth/v1/recover', {
        body: { email: pendingEmail, redirect_to: recoveryCallbackUrl() }
      }).then(function (response) {
        if (!response.ok) throw new Error();
        showMessage('password', 'Check your email for a password reset link.');
      }).catch(function () {
        showMessage('password', 'We couldn’t send the reset email. Please try again.');
      });
      return;
    }
    if (target.closest('[data-gettmi-auth-cancel-recovery]')) {
      var cancelledSession = recoverySession || readSession();
      var cancelCfg = config();
      if (cancelCfg && cancelledSession && cancelledSession.access_token) {
        request(cancelCfg, '/auth/v1/logout', { body: {}, token: cancelledSession.access_token }).catch(function () {});
      }
      recoverySession = null;
      writeSession(null);
      cleanRecoveryUrl();
      showSignedOut();
      dispatchSignedOut(cancelledSession);
      return;
    }
    if (target.closest('[data-gettmi-signout]')) {
      var logoutCfg = config();
      var session = readSession();
      if (logoutCfg && session && session.access_token) {
        request(logoutCfg, '/auth/v1/logout', { body: {}, token: session.access_token }).catch(function () {});
      }
      recoverySession = null;
      writeSession(null);
      showSignedOut();
      dispatchSignedOut(session);
    }
  }, true);

  document.addEventListener('submit', function (event) {
    var emailForm = event.target.closest('[data-gettmi-auth-email-form]');
    var passwordForm = event.target.closest('[data-gettmi-auth-password-form]');
    var signupForm = event.target.closest('[data-gettmi-auth-signup-form]');
    var codeForm = event.target.closest('[data-gettmi-auth-code-form]');
    var confirmForm = event.target.closest('[data-gettmi-auth-confirm-form]');
    var recoveryForm = event.target.closest('[data-gettmi-auth-recovery-form]');
    if (!emailForm && !passwordForm && !signupForm && !codeForm && !confirmForm && !recoveryForm) return;
    event.preventDefault();

    var cfg = config();
    if (!cfg) return;

    if (recoveryForm) {
      var nextPassword = recoveryForm.password.value;
      var confirmation = recoveryForm.confirm_password.value;
      if (!recoverySession || !recoverySession.access_token) {
        showMessage('recovery', 'This password link is missing, expired or has already been used. Request a new one from Log in.');
        return;
      }
      if (nextPassword.length < 10 || !/[a-z]/.test(nextPassword) || !/[A-Z]/.test(nextPassword) || !/\d/.test(nextPassword)) {
        showMessage('recovery', 'Use at least 10 characters, including an uppercase letter, a lowercase letter and a number.');
        return;
      }
      if (nextPassword !== confirmation) {
        showMessage('recovery', 'Passwords do not match.');
        return;
      }
      setBusy(recoveryForm, true, 'Updating...');
      showMessage('recovery', '');
      request(cfg, '/auth/v1/user', {
        method: 'PUT',
        token: recoverySession.access_token,
        body: { password: nextPassword }
      }).then(function (response) {
        if (!response.ok) throw new Error();
        recoveryForm.reset();
        cleanRecoveryUrl();
        showMessage('recovery', 'Password updated. Taking you to your profile...');
        var completedSession = recoverySession;
        recoverySession = null;
        window.setTimeout(function () {
          routeSignedIn(completedSession).then(function (routedSession) {
            if (!routedSession) return;
            completeFreshSignIn(completedSession);
          });
        }, 450);
      }).catch(function () {
        showMessage('recovery', 'Your password could not be updated. Request a new link and try again.');
      }).finally(function () {
        setBusy(recoveryForm, false);
      });
      return;
    }

    if (emailForm) {
      pendingEmail = emailForm.email.value.trim().toLowerCase();
      if (!pendingEmail) return;
      showAuthStep('password');
      return;
    }

    if (passwordForm) {
      setBusy(passwordForm, true, 'Checking...');
      showMessage('password', '');
      request(cfg, '/auth/v1/token?grant_type=password', {
        body: { email: pendingEmail, password: passwordForm.password.value }
      }).then(function (response) {
        if (!response.ok) throw new Error();
        return response.json();
      }).then(function (data) {
        finishSignIn(data, pendingEmail);
        passwordForm.reset();
      }).catch(function () {
        showMessage('password', 'Email or password is incorrect. Try again, use a verification code or reset your password.');
      }).finally(function () {
        setBusy(passwordForm, false);
      });
      return;
    }

    if (signupForm) {
      var email = signupForm.email.value.trim().toLowerCase();
      var password = signupForm.password.value;
      if (!isValidEmailAddress(email)) {
        showMessage('signup', 'Enter a valid email address.');
        return;
      }
      if (isDisposableEmailDomain(emailDomain(email))) {
        showMessage('signup', 'Please use your personal email address.');
        return;
      }
      if (password.length < 10 || !/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/\d/.test(password)) {
        showMessage('signup', 'Use at least 10 characters, including an uppercase letter, a lowercase letter and a number.');
        return;
      }
      setBusy(signupForm, true, 'Creating...');
      showMessage('signup', '');
      request(cfg, '/auth/v1/signup', {
        body: { email: email, password: password, email_redirect_to: authCallbackUrl() }
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      }).then(function (data) {
        if (data.access_token) {
          finishSignIn(data, email);
          return;
        }
        /* Confirmation is on, so Supabase withholds the session until the emailed
           code is entered. Give that its own screen, as the app does. */
        pendingEmail = email;
        showAuthStep('signup-pending');
      }).catch(function (error) {
        showMessage('signup', signupFailureMessage(error));
      }).finally(function () {
        setBusy(signupForm, false);
      });
      return;
    }

    if (confirmForm) {
      setBusy(confirmForm, true, 'Verifying...');
      showMessage('signup-pending', '');
      setCodeError('signup-pending', false);
      request(cfg, '/auth/v1/verify', {
        body: { type: 'signup', email: pendingEmail, token: confirmForm.code.value.trim() }
      }).then(function (response) {
        if (!response.ok) throw new Error();
        return response.json();
      }).then(function (data) {
        finishSignIn(data, pendingEmail);
        confirmForm.reset();
        resetCodeEntry('signup-pending');
      }).catch(function () {
        setCodeError('signup-pending', true);
        showMessage('signup-pending', 'That code did not work. Check it and try again.');
      }).finally(function () {
        setBusy(confirmForm, false);
      });
      return;
    }

    if (codeForm) {
      setBusy(codeForm, true, 'Verifying...');
      showMessage('code', '');
      setCodeError('code', false);
      request(cfg, '/auth/v1/verify', {
        body: { type: 'email', email: pendingEmail, token: codeForm.code.value.trim() }
      }).then(function (response) {
        if (!response.ok) throw new Error();
        return response.json();
      }).then(function (data) {
        finishSignIn(data, pendingEmail);
        codeForm.reset();
        resetCodeEntry('code');
      }).catch(function () {
        setCodeError('code', true);
        showMessage('code', 'That code did not work. Check it and try again.');
      }).finally(function () {
        setBusy(codeForm, false);
      });
    }
  });

  document.addEventListener('input', function (event) {
    if (event.target.matches('[data-gettmi-profile-search]')) filterProfile(event.target.value);
    if (event.target.matches('[data-gettmi-auth-signup-form] input[name="email"]')) {
      renderEmailSuggestion(event.target.closest('[data-gettmi-auth-signup-form]'));
    }
    if (event.target.matches('[data-gettmi-auth-code-row] [name="code"]')) {
      var typedStep = stepOf(event.target);
      event.target.value = event.target.value.replace(/\D/g, '').slice(0, 6);
      setCodeError(typedStep, false);
      showMessage(typedStep, '');
      renderCodeBoxes(typedStep);
    }
  });

  document.addEventListener('gettmi:onboarding-complete', function (event) {
    var detail = event.detail || {};
    var session = detail.session || readSession();
    if (!session) {
      showSignedOut();
      return;
    }
    routeId += 1;
    /* Finishing the quiz drops you into the storefront, the way finishing it in the
       app drops you into the app, rather than leaving you sat in Settings. */
    if (desktopAccountMode()) {
      showProfile(session);
    } else {
      closeSettings();
      updateProfileData(config(), session);
    }
    document.dispatchEvent(new CustomEvent('gettmi:profile-updated', {
      detail: { profile: detail.profile || {}, session: session }
    }));
  });

  document.addEventListener('gettmi:onboarding-cancel', function () {
    var logoutCfg = config();
    var session = readSession();
    if (logoutCfg && session && session.access_token) {
      request(logoutCfg, '/auth/v1/logout', { body: {}, token: session.access_token }).catch(function () {});
    }
    recoverySession = null;
    writeSession(null);
    showSignedOut();
    dispatchSignedOut(session);
  });

  document.addEventListener('gettmi:auth-required', openSettings);
  document.addEventListener('gettmi:signup-required', function () {
    /* refreshState settles on the welcome step, so wait for it before overriding,
       otherwise the create-account step is replaced a moment after it appears. */
    Promise.resolve(openSettings()).then(function () {
      if (!readSession()) showAuthStep('signup');
    });
  });
  document.addEventListener('gettmi:profile-updated', function (event) {
    var detail = event.detail || {};
    renderSharedProfileAvatars(detail.profile || {}, detail.session || readSession());
  });
  document.addEventListener('gettmi:signed-out', function () {
    renderSharedProfileAvatars({}, null);
  });

  function initialise() {
    readOAuthCallback().then(function (callback) {
      if (callback && callback.recovery) {
        var recoveryRoot = overlay();
        if (!recoveryRoot) return;
        recoverySession = callback.session;
        recoveryRoot.hidden = false;
        recoveryRoot.querySelector('[data-gettmi-auth-gate]').hidden = false;
        recoveryRoot.querySelector('[data-gettmi-profile]').hidden = true;
        document.documentElement.classList.add('gettmi-overlay-open');
        showAuthStep('recovery');
        if (callback.error) showMessage('recovery', callback.error);
        return;
      }
      var oauthSession = callback && callback.session;
      if (oauthSession) {
        /* Returning from a provider lands on the storefront. Only onboarding opens the overlay,
           so the login step is never shown to someone who has just signed in. */
        return routeSignedIn(oauthSession, { fresh: true }).then(function (routedSession) {
          if (!routedSession) return;
          return completeFreshSignIn(oauthSession);
        });
      }
      return validSession().then(function (session) {
        if (session) {
          syncBag(config(), session);
          document.dispatchEvent(new CustomEvent('gettmi:signed-in', {
            detail: { session: session }
          }));
          return updateProfileData(config(), session);
        }
        renderSharedProfileAvatars({}, null);
        return null;
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise);
  } else {
    initialise();
  }
})();
